import { memo, useCallback, useState } from 'react'
import { DatePicker as MDDatePicker, DatePickerProps as IDatePickerProps } from '@mui/x-date-pickers/DatePicker'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { isValid } from 'date-fns'
import { mergeSlotProps } from 'utils/slotProps'
import styles from './DatePicker.pcss'

export interface DatePickerProps {
  value: Date | null
  minDate?: Date
  MDPickerProps?: Partial<IDatePickerProps<any>>
  disabled?: boolean
  className?: string
  calendarClassName?: string
  onChange: (value: Date) => void
}

// EXPL: memoized so callers that keep their props stable (memoized `value`/`minDate`, `useCallback`
// handlers) stop re-rendering the picker every time an unrelated part of their state changes.
export const DatePicker = memo(function DatePicker(props: DatePickerProps) {
  const [open, setOpen] = useState(false)
  const { slotProps: callerSlotProps, ...pickerProps } = props.MDPickerProps || {}
  const { onChange, calendarClassName } = props

  // EXPL: the text field is editable, so MUI hands us `null` when it is cleared and an Invalid Date
  // while a segment is half-typed. Callers are typed `(value: Date) => void` and go straight to
  // `.toISOString()`, so both are dropped here and the last valid value stands.
  const onDateChange = useCallback((value: Date | null) => {
    if (value && isValid(value)) {
      onChange(value)
    }
  }, [onChange])

  const openPicker = () => {
    setOpen(true)
  }

  const closePicker = () => {
    setOpen(false)
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <div className={`${props.className || ''} ${styles.box}`}>
        <MDDatePicker
          {...pickerProps}
          open={open}
          value={props.value}
          disabled={props.disabled}
          minDate={props.minDate}
          className={`${styles.root} ${props.disabled ? styles.disabled : ''}`}
          slotProps={mergeSlotProps({
            textField: {
              InputProps: { classes: { root: styles.pointer }, 'data-testid': 'date-picker-input' } as any,
              onClick: openPicker
            },
            inputAdornment: {
              className: styles['icon-btn']
            },
            // EXPL: the calendar renders in a portal outside the caller's DOM, so callers that need
            // to recognise a click inside it (RecycleSelector's click-away guard) tag it by class.
            // Both surfaces are tagged because the picker renders in a popper on desktop and in a
            // dialog on mobile.
            popper: {
              className: calendarClassName
            },
            mobilePaper: {
              className: calendarClassName
            }
          }, callerSlotProps) as any}
          reduceAnimations
          onChange={onDateChange}
          onOpen={openPicker}
          onClose={closePicker}
        />
      </div>
    </LocalizationProvider>
  )
})

export default DatePicker
